// IngredientManager.jsx
import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaUndoAlt, FaLink, FaUnlink } from 'react-icons/fa';
import api from '../services/api.service';
import './IngredientManagement.css';

const IngredientManagement = () => {
  const [ingredients, setIngredients] = useState([]);
  const [menuItems, setMenuItems] = useState([]); // Changed from products to menuItems
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('true');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Malzeme formu state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
    unit: 'gr',
    menuItemId: '', // Changed from productId to menuItemId
    costPerUnit: '',
    isActive: true
  });
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  
  // Verileri yükle
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [ingredientsRes, menuItemsRes] = await Promise.all([
          api.getIngredients(),
          api.getMenuItems({ isActive: true }) // Changed from getProducts to getMenuItems
        ]);
        
        setIngredients(ingredientsRes.data);
        setMenuItems(menuItemsRes.data); // Changed from setProducts to setMenuItems
        setError(null);
      } catch (err) {
        console.error("Veri yükleme hatası:", err);
        setError("Veriler yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Malzemeleri filtrele
  const filteredIngredients = ingredients.filter(ingredient => {
    // Aktifliğe göre filtrele
    if (filterActive !== 'all') {
      const isActiveFilter = filterActive === 'true';
      if (ingredient.isActive !== isActiveFilter) {
        return false;
      }
    }
    
    // Arama terimine göre filtrele
    if (searchTerm && !ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });
  
  // Filtreleri sıfırla
  const resetFilters = () => {
    setSearchTerm('');
    setFilterActive('true');
  };
  
  // Form değişikliklerini işle
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Yeni malzeme ekleme formunu göster
  const handleShowAddForm = () => {
    setFormData({
      id: null,
      name: '',
      description: '',
      unit: 'gr',
      menuItemId: '', // Changed from productId to menuItemId
      costPerUnit: '',
      isActive: true
    });
    setFormMode('add');
    setShowForm(true);
  };
  
  // Malzeme düzenleme formunu göster
  const handleShowEditForm = (ingredient) => {
    setFormData({
      id: ingredient.id,
      name: ingredient.name,
      description: ingredient.description || '',
      unit: ingredient.unit || 'gr',
      menuItemId: ingredient.menuItemId ? String(ingredient.menuItemId) : '', // Changed from productId
      costPerUnit: ingredient.costPerUnit || '',
      isActive: ingredient.isActive
    });
    setFormMode('edit');
    setShowForm(true);
  };
  
  // Formu kapat
  const handleCloseForm = () => {
    setShowForm(false);
  };
  
  // Formu gönder
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Veriyi hazırla
      const submissionData = {
        ...formData,
        costPerUnit: formData.costPerUnit ? parseFloat(formData.costPerUnit) : null,
        menuItemId: formData.menuItemId || null // Changed from productId
      };
      
      let response;
      
      if (formMode === 'add') {
        response = await api.createIngredient(submissionData);
        setIngredients([...ingredients, response.data]);
      } else {
        response = await api.updateIngredient(formData.id, submissionData);
        
        // Malzemeler listesini güncelle
        setIngredients(ingredients.map(ingredient => 
          ingredient.id === response.data.id ? response.data : ingredient
        ));
      }
      
      // Formu kapat
      setShowForm(false);
      setError(null);
    } catch (err) {
      console.error("Malzeme kaydetme hatası:", err);
      setError("Malzeme kaydedilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };
  
  // Malzeme sil
  const handleDeleteIngredient = async (id) => {
    if (!window.confirm('Bu malzemeyi silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.deleteIngredient(id);
      
      // Malzemeler listesini güncelle
      setIngredients(ingredients.filter(ingredient => ingredient.id !== id));
      setError(null);
    } catch (err) {
      console.error("Malzeme silme hatası:", err);
      setError("Malzeme silinirken bir hata oluştu. Bu malzeme bir reçetede kullanılıyor olabilir.");
    } finally {
      setLoading(false);
    }
  };
  
  // Menü ürünü adını bul
  const getMenuItemName = (menuItemId) => { // Changed from getProductName
    if (!menuItemId) return '';
    const menuItem = menuItems.find(item => item.id === menuItemId); // Changed from products to menuItems
    return menuItem ? menuItem.name : '';
  };
  
  return (
    <div className="ingredient-manager">
      {error && <div className="error-message">{error}</div>}
      
      <div className="actions-bar">
        <div className="filter-container">
          <div className="filter-group">
            <label className="filter-label">Durum</label>
            <select
              className="filter-select"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              disabled={loading}
            >
              <option value="true">Aktif Malzemeler</option>
              <option value="false">Pasif Malzemeler</option>
              <option value="all">Tüm Malzemeler</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Malzeme Ara</label>
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="İsme göre ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          
          <button 
            className="reset-button"
            onClick={resetFilters}
            disabled={filterActive === 'true' && !searchTerm || loading}
          >
            <FaUndoAlt className="button-icon" />
            Temizle
          </button>
        </div>
        
        <button 
          className="add-button"
          onClick={handleShowAddForm}
          disabled={loading}
        >
          <FaPlus className="button-icon" />
          Yeni Malzeme
        </button>
      </div>
      
      {/* Malzeme Formu */}
      {showForm && (
        <div className="form-container">
          <form onSubmit={handleSubmitForm} className="form">
            <div className="form-header">
              <h3>{formMode === 'add' ? 'Yeni Malzeme' : 'Malzeme Düzenle'}</h3>
              <button 
                type="button" 
                className="close-button"
                onClick={handleCloseForm}
              >
                &times;
              </button>
            </div>
            
            <div className="form-body">
              <div className="form-group">
                <label htmlFor="name">Malzeme Adı</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Açıklama</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  disabled={loading}
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="unit">Birim</label>
                  <select
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleFormChange}
                    required
                    disabled={loading}
                  >
                    <option value="gr">Gram (gr)</option>
                    <option value="kg">Kilogram (kg)</option>
                    <option value="ml">Mililitre (ml)</option>
                    <option value="lt">Litre (lt)</option>
                    <option value="adet">Adet</option>
                    <option value="paket">Paket</option>
                    <option value="dilim">Dilim</option>
                    <option value="çay kaşığı">Çay Kaşığı</option>
                    <option value="yemek kaşığı">Yemek Kaşığı</option>
                    <option value="bardak">Bardak</option>
                    <option value="su bardağı">Su Bardağı</option>
                    <option value="çay bardağı">Çay Bardağı</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="costPerUnit">Birim Maliyet (₺)</label>
                  <input
                    type="number"
                    id="costPerUnit"
                    name="costPerUnit"
                    value={formData.costPerUnit}
                    onChange={handleFormChange}
                    step="0.01"
                    min="0"
                    disabled={loading}
                    placeholder="Opsiyonel"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="menuItemId">İlişkili Menü Ürünü</label>
                  <select
                    id="menuItemId"
                    name="menuItemId"
                    value={formData.menuItemId}
                    onChange={handleFormChange}
                    disabled={loading}
                  >
                    <option value="">Menü Ürünü Seçin (Opsiyonel)</option>
                    {menuItems.map(menuItem => (
                      <option key={menuItem.id} value={menuItem.id.toString()}>
                        {menuItem.name} - ₺{menuItem.price}
                      </option>
                    ))}
                  </select>
                  <p className="form-help-text">
                    Menü ürünü seçilirse bu malzeme o ürünün reçetesinde kullanılıyor olarak işaretlenir.
                  </p>
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleFormChange}
                      disabled={loading}
                    />
                    Aktif
                  </label>
                </div>
              </div>
            </div>
            
            <div className="form-footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCloseForm}
                disabled={loading}
              >
                İptal
              </button>
              <button
                type="submit"
                className="save-button"
                disabled={loading}
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {loading && !showForm ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Veriler yükleniyor...</p>
        </div>
      ) : filteredIngredients.length > 0 ? (
        <div className="ingredient-table-container">
          <table className="ingredient-table">
            <thead>
              <tr>
                <th>Malzeme Adı</th>
                <th>Birim</th>
                <th>Birim Maliyet</th>
                <th>İlişkili Menü Ürünü</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredIngredients.map(ingredient => (
                <tr key={ingredient.id} className={!ingredient.isActive ? 'inactive-row' : ''}>
                  <td>
                    <div className="ingredient-name">
                      {ingredient.name}
                      {ingredient.description && (
                        <div className="ingredient-description">{ingredient.description}</div>
                      )}
                    </div>
                  </td>
                  <td>{ingredient.unit}</td>
                  <td>{ingredient.costPerUnit ? `₺${parseFloat(ingredient.costPerUnit).toFixed(2)}` : '-'}</td>
                  <td>
                    {ingredient.menuItemId ? (
                      <div className="linked-product">
                        <FaLink className="link-icon" />
                        {getMenuItemName(ingredient.menuItemId)}
                      </div>
                    ) : (
                      <div className="no-product">
                        <FaUnlink className="link-icon" />
                        Bağlı değil
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${ingredient.isActive ? 'active' : 'inactive'}`}>
                      {ingredient.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button 
                        className="edit-button"
                        onClick={() => handleShowEditForm(ingredient)}
                        disabled={loading}
                        title="Düzenle"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteIngredient(ingredient.id)}
                        disabled={loading}
                        title="Sil"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="info-message">
          {searchTerm || filterActive !== 'true'
            ? 'Seçilen filtrelere uygun malzeme bulunamadı.' 
            : 'Henüz malzeme eklenmemiş. "Yeni Malzeme" butonu ile malzeme ekleyebilirsiniz.'
          }
        </div>
      )}
    </div>
  );
};

export default IngredientManagement;